import {
  ArticleDetailUtil,
  EArticleClass,
  TArticleDetail,
  IGalleryArticleDetail,
  IImage,
  IRichArticleDetail,
  IVideoArticleDetail,
  VideoUtils,
} from 'shared/models';
import { ImageObject, SocialMediaPosting, VideoObject } from 'schema-dts';
import { Region } from '@lemon8/web-app-shared/i18n';
import type { TFunction } from 'i18next';
import { removeEscapedSpecialXmlCharacter } from 'shared/utils/escape-xml';
import { dateToLocalISO } from 'shared/utils/format/time-formatter';
import { IArticleGPTInfo } from 'shared/atoms';

export const ArticleJsonLDUtil = {
  getTitle: (props: {
    article: TArticleDetail;
    region: Region;
    t: TFunction;
    abInfo: Record<string, boolean>;
    gptInfo?: IArticleGPTInfo & {
      showGptTitle: boolean;
      showGptDescription: boolean;
      showGptSupplements: boolean;
    };
  }): string => {
    const { article, t, gptInfo } = props;

    if (gptInfo?.showGptTitle) {
      return gptInfo.title;
    }

    const articleTitle = article.title;
    if (articleTitle) {
      return removeEscapedSpecialXmlCharacter(articleTitle.replace(/\s+/g, ' '));
    }

    // 3. fallback title according to article class
    if (article.articleClass === EArticleClass.GALLERY) {
      return `${t('user_published_gallery').replace('%s', article.author.nickName)} | Lemon8`;
    } else if (article.articleClass === EArticleClass.VIDEO) {
      return `${t('own_video').replace('%s', article.author.nickName)} | Lemon8`;
    } else {
      return `${t('user_published_article').replace('%s', article.author.nickName)} | Lemon8`;
    }
  },
  getDescription: (props: {
    article: TArticleDetail;
    region: Region;
    t: TFunction;
    abInfo: Record<string, boolean>;
    gptInfo?: IArticleGPTInfo & {
      showGptTitle: boolean;
      showGptDescription: boolean;
      showGptSupplements: boolean;
    };
  }): string => {
    const { article, gptInfo } = props;

    if (gptInfo?.showGptDescription) {
      return gptInfo.description;
    }

    const articleShortContent = article.shortContent;
    if (articleShortContent) {
      return removeEscapedSpecialXmlCharacter(articleShortContent.replace(/\s+/g, ' '));
    }
    return ArticleJsonLDUtil.getTitle(props);
  },
  getKeywords: (props: { article: TArticleDetail; region: Region; t: TFunction }): string[] => {
    const { article } = props;

    const result: string[] = [];
    // const { relatedQueryInfos } = article;

    const articleTitle = article.title;
    if (articleTitle) {
      result.push(articleTitle);
    }

    result.push(article.author.nickName);
    // result.push(...relatedQueryInfos.map((info) => info.word));
    result.push('Lemon8');
    return result;
  },
  getSocialMediaJsonLD: (props: {
    article: TArticleDetail;
    region: Region;
    t: TFunction;
    abInfo: Record<string, boolean>;
    gptInfo: IArticleGPTInfo & {
      showGptTitle: boolean;
      showGptDescription: boolean;
      showGptSupplements: boolean;
    };
  }): SocialMediaPosting => {
    // region: page on the URL query
    const { article, region, t, abInfo, gptInfo } = props;
    const articleTranslated = ArticleDetailUtil.isTranslatedArticleToShow(article, region);
    const { hitGptComment = false } = abInfo;
    const { comments: gptComments = [] } = gptInfo;

    const jsonLDTitle = ArticleJsonLDUtil.getTitle(props);
    const jsonLDDescription = ArticleJsonLDUtil.getDescription(props);
    const jsonLDKeywords = ArticleJsonLDUtil.getKeywords(props);
    const canonicalUrl = `https://www.lemon8-app.com${ArticleDetailUtil.getArticlePath(article)}?region=${
      articleTranslated ? region : article.articleRegion
    }`;

    const articleTitle = article.title;

    const {
      readCount,
      diggCount,
      favoriteCount,
      commentCount: originalCommentCount,
      publishTime,
      modifyTime,
      gptSummary,
    } = article;

    const commentCount = hitGptComment ? originalCommentCount + gptComments.length : originalCommentCount;

    const imageList: ImageObject[] = [];

    if (article.articleClass === EArticleClass.GALLERY) {
      const { imageList: galleryImageList, galleryLabels, ocrText = [] } = article as IGalleryArticleDetail;

      const imageListWithExtraInfo = galleryImageList.map((image, index) => {
        return {
          ...image,
          imgOcrText: ocrText[index] || '',
        };
      });

      imageList.push(
        ...imageListWithExtraInfo.map((image: IImage & { imgOcrText: string }, index: number): ImageObject => {
          const galleryKeywords = galleryLabels
            .filter((label) => label.imageIndex === index)
            .map((label) => label.name);

          return {
            '@type': 'ImageObject',
            url: image.url,
            contentUrl: image.url,
            representativeOfPage: 'True',
            keywords: [...galleryKeywords, articleTitle, article.author.nickName, 'Lemon8'],
            caption:
              gptInfo.imageDescription?.[index] ||
              t('own_picture_with_count')
                .replace('%s', articleTitle)
                .replace('%d', String(index + 1)),
            embeddedTextCaption: !articleTranslated ? image.imgOcrText || '' : '',
          };
        }),
      );
    } else {
      const { largeImage } = article as IVideoArticleDetail | IRichArticleDetail;
      imageList.push({
        '@type': 'ImageObject',
        url: largeImage.url,
        contentUrl: largeImage.url,
        representativeOfPage: 'True',
        keywords: [articleTitle, article.author.nickName, 'Lemon8'],
        caption:
          gptInfo.imageDescription?.[0] || t('own_picture_with_count').replace('%s', articleTitle).replace('%d', '1'),
      } as ImageObject);
    }

    const articleBaseJsonLD: SocialMediaPosting = {
      '@type': 'SocialMediaPosting',
      headline: jsonLDTitle,
      description: jsonLDDescription,
      keywords: jsonLDKeywords,
      url: canonicalUrl,
      commentCount,

      datePublished: dateToLocalISO(publishTime, /** currently fix timezone to JP. @lidiang */ 9),

      image: imageList,
      sharedContent: imageList,

      mainEntityOfPage: {
        '@id': canonicalUrl,
        '@type': 'WebPage',
      },
      interactionStatistic: [
        {
          '@type': 'InteractionCounter',
          interactionType: {
            '@type': 'ViewAction',
          },
          userInteractionCount: readCount,
        },
        {
          '@type': 'InteractionCounter',
          interactionType: {
            '@type': 'LikeAction',
          },
          userInteractionCount: diggCount,
        },
        {
          '@type': 'InteractionCounter',
          interactionType: {
            '@type': 'BookmarkAction',
          },
          userInteractionCount: favoriteCount,
        },
      ],
    };

    if (modifyTime) {
      articleBaseJsonLD.dateModified = dateToLocalISO(modifyTime, /** currently fix timezone to JP. @lidiang */ 9);
    }

    if (gptSummary) {
      articleBaseJsonLD.abstract = gptSummary;
    }

    return articleBaseJsonLD;
  },
  getVideoObjectJsonLD: (props: {
    article: IVideoArticleDetail;
    region: Region;
    t: TFunction;
    abInfo: Record<string, boolean>;
    gptInfo?: IArticleGPTInfo & {
      showGptTitle: boolean;
      showGptDescription: boolean;
      showGptSupplements: boolean;
    };
  }): VideoObject => {
    const { article } = props;
    const {
      video,
      readCount,
      diggCount,
      favoriteCount,
      largeImage,
      publishTime,
      gptSummary,
      isTranslatedVersion = false,
    } = article;

    const title = ArticleJsonLDUtil.getTitle(props);
    const description = ArticleJsonLDUtil.getDescription(props);

    const output: VideoObject = {
      '@type': 'VideoObject',
      name: title,
      description,
      thumbnailUrl: largeImage.url,
      uploadDate: dateToLocalISO(publishTime, /** currently fix timezone to JP. @lidiang */ 9),
      contentUrl: video.encryptedUrl || video.url,
      width: `${article.video.width}`,
      height: `${article.video.height}`,
      interactionStatistic: [
        {
          '@type': 'InteractionCounter',
          interactionType: {
            '@type': 'WatchAction',
          },
          userInteractionCount: readCount,
        },
        {
          '@type': 'InteractionCounter',
          interactionType: {
            '@type': 'LikeAction',
          },
          userInteractionCount: diggCount,
        },
        {
          '@type': 'InteractionCounter',
          interactionType: {
            '@type': 'BookmarkAction',
          },
          userInteractionCount: favoriteCount,
        },
      ],
    };

    if (article.video.duration) {
      output.duration = VideoUtils.durationTo8601(article.video.duration);
    }

    if (gptSummary) {
      output.abstract = gptSummary;
    }

    if (video.ocr?.length && !isTranslatedVersion) {
      output.embeddedTextCaption = video.ocr.join(', ');
    }

    return output;
  },
};
